import { decodeLocalSession, getLocalSessionToken, setLocalSessionToken } from '../../api/authToken';
import { markSecondFactorVerifiedSession } from '../../features/two-factor-auth/utils/pendingSecondFactorSession';
import {
  getImpersonationTargetMeta,
  isImpersonationSessionActive,
  markImpersonationEntering,
  setImpersonationSession,
} from './impersonationSession';

export const IMPERSONATION_HANDOFF_STORAGE_KEY = 'impersonation_handoff';
const HANDOFF_HASH_PREFIX = 'handoff=';

function encodePayload(payload) {
  return encodeURIComponent(JSON.stringify(payload));
}

function parseJsonPayload(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.ticket || !parsed?.userId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function decodeHashPayload(raw) {
  if (!raw) return null;
  try {
    return parseJsonPayload(decodeURIComponent(raw));
  } catch {
    return parseJsonPayload(raw);
  }
}

function readHashPayload() {
  if (typeof window === 'undefined') return null;
  const hash = String(window.location.hash || '').replace(/^#/, '');
  if (!hash.startsWith(HANDOFF_HASH_PREFIX)) return null;
  return decodeHashPayload(hash.slice(HANDOFF_HASH_PREFIX.length));
}

function stripHandoffHash() {
  if (typeof window === 'undefined') return;
  if (!String(window.location.hash || '').includes(HANDOFF_HASH_PREFIX)) return;
  const url = `${window.location.pathname}${window.location.search || ''}`;
  window.history.replaceState(null, '', url);
}

function readStoredHandoff() {
  if (typeof window === 'undefined') return null;
  try {
    return parseJsonPayload(sessionStorage.getItem(IMPERSONATION_HANDOFF_STORAGE_KEY));
  } catch {
    return null;
  }
}

function persistHandoff(payload) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(IMPERSONATION_HANDOFF_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

/** True while a one-time impersonation ticket is waiting to be claimed. */
export function hasPendingImpersonationHandoff() {
  return Boolean(readHashPayload() || readStoredHandoff());
}

export function clearImpersonationHandoff() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(IMPERSONATION_HANDOFF_STORAGE_KEY);
  } catch {
    // ignore
  }
  stripHandoffHash();
}

function isSameOrigin(origin) {
  if (typeof window === 'undefined' || !origin) return false;
  try {
    const target = new URL(origin, window.location.origin);
    return target.origin === window.location.origin;
  } catch {
    return false;
  }
}

export function buildImpersonationLandingUrl({
  origin,
  ticket,
  userId,
  companyId,
  target,
} = {}) {
  const payload = {
    ticket,
    userId,
    companyId: companyId || null,
    target: target || null,
  };
  const base = `${String(origin || '').replace(/\/+$/, '')}/app/impersonate`;

  if (isSameOrigin(origin)) {
    persistHandoff(payload);
    return base;
  }

  return `${base}#${HANDOFF_HASH_PREFIX}${encodePayload(payload)}`;
}

export function applyImpersonationSessionToken({ token, userId, companyId, target } = {}) {
  const session = decodeLocalSession(token);
  if (!session?.uid || !session.impersonatorAdminId) {
    throw new Error('Invalid impersonation session.');
  }
  if (userId && session.uid !== userId) {
    throw new Error('Impersonation session does not match the selected user.');
  }

  setLocalSessionToken(token);
  setImpersonationSession({
    userId: session.uid,
    companyId: companyId || null,
    adminUid: session.impersonatorAdminId || null,
    target: target || null,
  });
  markImpersonationEntering();
  markSecondFactorVerifiedSession(session.uid);
  return {
    token,
    userId: session.uid,
    companyId: companyId || null,
    target: target || null,
    uid: session.uid,
    email: session.email || target?.email || null,
    impersonationSessionId: session.impersonationSessionId || null,
  };
}

/**
 * Read a one-time ticket from the URL hash or sessionStorage before claim.
 * Safe to call during first render. Does not install a staff session token.
 */
export function consumeImpersonationHandoff() {
  const fromHash = readHashPayload();
  if (fromHash) {
    persistHandoff(fromHash);
    stripHandoffHash();
  }

  const payload = fromHash || readStoredHandoff();
  if (payload?.ticket && payload?.userId) {
    markImpersonationEntering();
    const existing = decodeLocalSession(getLocalSessionToken());
    const alreadyClaimed = Boolean(
      existing?.impersonatorAdminId && existing.uid === payload.userId
    );
    return {
      ...payload,
      alreadyClaimed,
    };
  }

  const existingToken = getLocalSessionToken();
  const existingSession = decodeLocalSession(existingToken);
  if (existingSession?.impersonatorAdminId && existingSession.uid) {
    markImpersonationEntering();
    markSecondFactorVerifiedSession(existingSession.uid);
    const meta = getImpersonationTargetMeta();
    return {
      alreadyClaimed: true,
      userId: existingSession.uid,
      companyId: meta.companyId || null,
      target: meta,
      uid: existingSession.uid,
      email: existingSession.email || meta.email || null,
    };
  }

  if (isImpersonationSessionActive() && existingSession?.uid) {
    return null;
  }

  return null;
}
